import { Bookmark, Bug, ChevronsUpDown, Copy, FileText, Layers, Pencil, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  AreaChart,
  BarChart,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuTrigger,
  DataTable,
  DonutChart,
  Drawer,
  Heatmap,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Label,
  LineChart,
  Panel,
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  Progress,
  RadioGroup,
  RadioGroupItem,
  ScrollArea,
  Separator,
  Slider,
  StackedBarChart,
  Switch,
  Toggle,
  Tooltip,
  formatBytes,
  formatClock,
  formatCount,
  formatDate,
  formatDuration,
  formatInt,
  formatMoney,
  formatMoneyCompact,
  formatMoneyExact,
  formatPercent,
  formatRelative,
  formatTokens,
} from '@orgus/ui';
import type { Column, HeatCell, Series } from '@orgus/ui';
import { Spec } from './Spec.tsx';

const DAY = 86_400_000;
/** A fixed clock so the catalogue renders the same every time (visual tests). */
const T0 = Date.UTC(2026, 8, 18);

/** Deterministic noise in 0..1. */
function wave(i: number, seed: number): number {
  const v = Math.sin(i * 12.9898 + seed * 78.233) * 43758.5453;
  return v - Math.floor(v);
}

/** Checkbox, Switch, RadioGroup, Slider, Toggle: every selection control in its states. */
function ControlsSection() {
  const [notify, setNotify] = useState(true);
  const [subagents, setSubagents] = useState(true);
  const [archive, setArchive] = useState(false);
  const [plan, setPlan] = useState('team');
  const [ceiling, setCeiling] = useState([70]);
  return (
    <Panel title="Selection controls" sub="components.md, Selection controls" data-catalogue="controls">
      <Spec label="Checkbox">
        <Checkbox checked={subagents} onCheckedChange={setSubagents} label="Include subagents" />
        <Checkbox checked={archive} onCheckedChange={setArchive} label="Archive on land" />
        <Checkbox checked={false} indeterminate onCheckedChange={() => undefined} label="Some projects" />
        <Checkbox checked={false} disabled onCheckedChange={() => undefined} label="Disabled" />
      </Spec>
      <Spec label="Switch (takes effect now)">
        <Label className="gap-2 text-fg">
          <Switch checked={notify} onCheckedChange={setNotify} /> Notify me when a session needs me
        </Label>
        <Label className="gap-2">
          <Switch size="sm" disabled /> Disabled
        </Label>
      </Spec>
      <Spec label="Radio group (in a form)">
        <RadioGroup value={plan} onValueChange={setPlan} aria-label="Plan">
          {[
            ['solo', 'Solo, one machine'],
            ['team', 'Team, shared hub'],
            ['org', 'Organisation, with SSO'],
          ].map(([v, l]) => (
            <Label key={v} className="gap-2 text-fg">
              <RadioGroupItem value={v!} /> {l}
            </Label>
          ))}
        </RadioGroup>
      </Spec>
      <Spec label="Slider">
        <div className="flex w-64 items-center gap-3">
          <Slider value={ceiling} onValueChange={setCeiling} max={100} step={5} aria-label="CPU ceiling" />
          <span className="w-10 text-right text-ui text-fg num">{formatPercent((ceiling[0] ?? 0) / 100)}</span>
        </div>
      </Spec>
      <Spec label="Toggle">
        <Toggle aria-label="Wrap lines" defaultPressed>
          Wrap
        </Toggle>
        <Toggle variant="outline" aria-label="Follow output">
          Follow
        </Toggle>
      </Spec>
      <Spec label="Progress">
        <div className="flex w-64 flex-col gap-1.5">
          <Progress value={62} aria-label="Importing the spec" />
          <span className="text-caption text-fg-subtle">Reading the spec: 62%</span>
        </div>
      </Spec>
    </Panel>
  );
}

/** Popover, HoverCard, ContextMenu, Command, Drawer: the overlays that are not dialogs. */
function OverlaySection() {
  const [drawer, setDrawer] = useState(false);
  // A value no item has, so cmdk highlights nothing (and scrolls nothing) until someone points or types.
  const [commandValue, setCommandValue] = useState('-');
  return (
    <Panel title="Anchored overlays" sub="components.md, Overlays: which one" data-catalogue="overlays">
      <Spec label="Popover">
        <Popover>
          <PopoverTrigger asChild>
            <Button iconRight={<ChevronsUpDown />}>Last 7 days</Button>
          </PopoverTrigger>
          <PopoverContent align="start">
            <PopoverHeader>
              <PopoverTitle>Date range</PopoverTitle>
              <PopoverDescription>Applies to every chart on the page.</PopoverDescription>
            </PopoverHeader>
          </PopoverContent>
        </Popover>
      </Spec>
      <Spec label="Hover card">
        <HoverCard openDelay={500} closeDelay={200}>
          <HoverCardTrigger asChild>
            <a href="#machines" className="text-ui text-accent-text underline underline-offset-4">
              studio
            </a>
          </HoverCardTrigger>
          <HoverCardContent>
            <p className="font-medium text-fg">studio</p>
            <p className="text-caption text-fg-subtle">Windows 11, 3 sessions, 42% CPU</p>
          </HoverCardContent>
        </HoverCard>
        <span className="text-caption text-fg-subtle">An extra only: never the one path to a fact</span>
      </Spec>
      <Spec label="Context menu">
        <ContextMenu>
          <ContextMenuTrigger className="flex h-control-lg w-64 items-center justify-center rounded-card border border-dashed border-border-strong text-caption text-fg-subtle">
            Right-click this row
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem>
              <Pencil /> Rename…
            </ContextMenuItem>
            <ContextMenuItem>
              <Copy /> Copy link <ContextMenuShortcut>Ctrl C</ContextMenuShortcut>
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem variant="destructive">
              <Trash2 /> Delete story
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      </Spec>
      <Spec label="Command">
        {/* Nothing is highlighted until you point or press a key: cmdk scrolls its highlighted item into view on mount, which scrolled this page to the middle. */}
        <Command className="w-80 rounded-card border border-border" value={commandValue} onValueChange={setCommandValue}>
          <CommandInput placeholder="Jump to a story or run a command" />
          <CommandList>
            <CommandEmpty>No matches.</CommandEmpty>
            <CommandGroup heading="Stories">
              <CommandItem>
                <Bookmark /> Import a requirements document
              </CommandItem>
              <CommandItem>
                <Bug /> Upload fails on a 20 MB image
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading="Actions">
              <CommandItem>
                <FileText /> Draft stories from a spec <CommandShortcut>Ctrl I</CommandShortcut>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </Spec>
      <Spec label="Drawer (Sheet)">
        <Button onClick={() => setDrawer(true)}>Open story</Button>
        <Drawer
          open={drawer}
          onOpenChange={setDrawer}
          title="Import a requirements document"
          description="Requirements, epic: Spec import"
          footer={
            <>
              <Button variant="ghost" className="ml-auto" onClick={() => setDrawer(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => setDrawer(false)}>
                Save story
              </Button>
            </>
          }
        >
          <div className="max-w-(--layout-width-prose) p-5 text-body text-fg-muted">
            A person drops a spec or a requirements document and the AI proposes epics and stories for review. Nothing is published until someone accepts it.
          </div>
        </Drawer>
      </Spec>
    </Panel>
  );
}

/** Accordion, Collapsible, Breadcrumb, Separator, ScrollArea. */
function DisclosureSection() {
  return (
    <Panel title="Disclosure and structure" data-catalogue="disclosure">
      <Spec label="Breadcrumb">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="#requirements">Requirements</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="#epic">Spec import</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Draft stories with AI</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </Spec>
      <Spec label="Accordion">
        <Accordion type="single" collapsible defaultValue="ac" className="w-full max-w-md">
          <AccordionItem value="ac">
            <AccordionTrigger>Acceptance criteria</AccordionTrigger>
            <AccordionContent>A dropped PDF, Word or Markdown file starts the import and shows a determinate progress banner.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="links">
            <AccordionTrigger>Links</AccordionTrigger>
            <AccordionContent>Blocks: Review drawer for proposed stories.</AccordionContent>
          </AccordionItem>
        </Accordion>
      </Spec>
      <Spec label="Collapsible">
        <Collapsible className="w-full max-w-md">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" iconRight={<ChevronsUpDown />}>
              Show 3 more facts
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="text-ui text-fg-muted">
            <p className="py-1">Owner: Srikanth</p>
            <p className="py-1">Estimate: 5 points</p>
            <p className="py-1">Updated: {formatDate(T0, T0)}</p>
          </CollapsibleContent>
        </Collapsible>
      </Spec>
      <Spec label="Separator">
        <div className="flex h-5 items-center gap-3 text-ui text-fg-muted">
          <span>12 stories</span>
          <Separator orientation="vertical" />
          <span>3 defects</span>
          <Separator orientation="vertical" />
          <span>2 need review</span>
        </div>
      </Spec>
      <Spec label="Scroll area">
        <ScrollArea className="h-28 w-64 rounded-card border border-border">
          <div className="p-3">
            {Array.from({ length: 12 }, (_, i) => (
              <p key={i} className="py-0.5 text-ui text-fg-muted num">
                attachment-{String(i + 1).padStart(2, '0')}.png
              </p>
            ))}
          </div>
        </ScrollArea>
      </Spec>
    </Panel>
  );
}

interface Story {
  id: string;
  kind: 'story' | 'defect';
  title: string;
  epic: string;
  owner: string;
  points: number | null;
  updated: number;
}

const EPICS = ['Spec import', 'AI drafting', 'Attachments', 'Backlog'];
const TITLES = ['Parse headings into epics', 'Propose stories with criteria', 'Flag duplicates', 'Upload an image', 'Preview a PDF', 'Retry a failed upload', 'Group by epic', 'Bulk accept drafts'];

/** The DataTable: a backlog grouped by epic, 2,000 rows, virtualised above 100. */
function TableSection() {
  const rows = useMemo<Story[]>(
    () =>
      Array.from({ length: 2000 }, (_, i) => ({
        id: `REQ-${1000 + i}`,
        kind: wave(i, 1) > 0.8 ? 'defect' : 'story',
        title: `${TITLES[i % TITLES.length]} ${Math.floor(i / TITLES.length) + 1}`,
        epic: EPICS[i % EPICS.length]!,
        owner: ['Srikanth', 'Claude', ''][i % 3]!,
        points: wave(i, 2) > 0.15 ? Math.ceil(wave(i, 3) * 8) : null,
        updated: T0 - Math.floor(wave(i, 4) * 20 * DAY),
      })),
    [],
  );
  const columns = useMemo<Column<Story>[]>(
    () => [
      {
        key: 'kind',
        header: 'Type',
        srHeader: true,
        cell: (s) => (
          <Tooltip content={s.kind === 'defect' ? 'Defect' : 'Story'}>
            <span tabIndex={-1} className="inline-flex text-fg-subtle [&_svg]:size-4">
              {s.kind === 'defect' ? <Bug aria-label="Defect" /> : <Bookmark aria-label="Story" />}
            </span>
          </Tooltip>
        ),
      },
      { key: 'id', header: 'ID', cell: (s) => <span className="font-mono text-caption text-fg-subtle">{s.id}</span>, text: (s) => s.id, sortValue: (s) => s.id },
      { key: 'title', header: 'Title', grow: true, cell: (s) => <span className="block truncate font-medium text-fg">{s.title}</span>, text: (s) => s.title, sortValue: (s) => s.title },
      { key: 'owner', header: 'Owner', cell: (s) => s.owner, text: (s) => s.owner, sortValue: (s) => s.owner || null, hideBelow: 'md' },
      { key: 'points', header: 'Estimate', type: 'number', cell: (s) => (s.points == null ? '' : formatInt(s.points)), sortValue: (s) => s.points },
      { key: 'updated', header: 'Updated', type: 'date', cell: (s) => formatRelative(s.updated, T0), sortValue: (s) => s.updated },
    ],
    [],
  );
  return (
    <Panel title="DataTable" sub="TanStack Table and Virtual: 2,000 rows grouped by epic, only the rows in view render" flush className="xl:col-span-2" data-catalogue="table">
      <DataTable
        frameless
        label="Backlog"
        rows={rows}
        columns={columns}
        rowKey={(s) => s.id}
        searchable={{ placeholder: 'Search stories' }}
        groupBy={(s) => s.epic}
        renderGroup={(epic, list) => {
          const defects = list.filter((s) => s.kind === 'defect').length;
          return (
            <span className="flex items-center gap-2 text-ui">
              <Layers className="size-4 text-fg-subtle" aria-hidden />
              <span className="font-medium text-fg">{epic}</span>
              <span className="text-caption text-fg-subtle num">
                {formatCount(list.length - defects)} stories, {formatCount(defects)} defects
              </span>
            </span>
          );
        }}
        pageSize={false}
        initialSort={{ key: 'updated', dir: 'desc' }}
        onRowClick={() => undefined}
        rowLabel={(s) => `Open ${s.id}`}
      />
    </Panel>
  );
}

const MODELS = [
  { key: 'opus', label: 'Opus' },
  { key: 'sonnet', label: 'Sonnet' },
  { key: 'haiku', label: 'Haiku' },
];
const ORDER = MODELS.map((m) => m.key);

/** The chart family on demo data, plus the states. */
function ChartsSection() {
  const spend: Series[] = MODELS.map((m, mi) => ({
    key: m.key,
    label: m.label,
    points: Array.from({ length: 30 }, (_, i) => ({ x: T0 + i * DAY, y: Math.round((40 - mi * 12) * (1 + i / 30) + wave(i, mi) * 18) })),
  }));
  const total: Series[] = [{ key: 'total', label: 'Total', points: Array.from({ length: 30 }, (_, i) => ({ x: T0 + i * DAY, y: Math.round(60 + i * 6 + wave(i, 9) * 30) })) }];
  const tokens = Array.from({ length: 14 }, (_, i) => ({
    label: formatDate(T0 + i * DAY, T0),
    values: Object.fromEntries(MODELS.map((m, mi) => [m.key, Math.round((3 - mi) * 400_000 * (0.6 + wave(i, mi + 3)))])),
  }));
  const projects = ['Qawl-Agents', 'billing-service', 'Orgus', 'docs-site', 'mobile', 'infra', 'design', 'web-app', 'data'].map((p, i) => ({
    key: p,
    label: p,
    value: Math.round(900 / (i + 1) + wave(i, 5) * 40),
  }));
  const cells: HeatCell[] = [];
  for (let d = 0; d < 7; d++) for (let h = 0; h < 24; h++) cells.push({ day: d, hour: h, value: d < 5 && h >= 8 && h <= 19 ? Math.round(wave(d * 24 + h, 7) * 9 + 3) : Math.round(wave(d * 24 + h, 8) * 2) });

  return (
    <div className="grid items-start gap-5 xl:grid-cols-2" data-catalogue="charts">
      <LineChart
        title="Spend per day by model, last 30 days"
        summary="Opus spend rose from about $40 to $90 a day; Sonnet and Haiku stayed lower"
        series={spend}
        order={ORDER}
        format={formatMoney}
        axisFormat={formatMoneyCompact}
        threshold={{ value: 90, label: 'Daily budget $90' }}
      />
      <AreaChart title="Total spend per day, last 30 days" summary="Total spend rose from $60 to $250 a day" series={total} format={formatMoney} axisFormat={formatMoneyCompact} />
      <StackedBarChart title="Tokens per day by model, last 14 days" summary="Opus used most tokens on every day" keys={MODELS} order={ORDER} data={tokens} />
      <BarChart title="Spend by project, this month" summary="Qawl-Agents spent the most this month" data={projects} format={formatMoney} limit={6} />
      <DonutChart title="Model mix, this month" summary="Opus is most of this month's spend" data={MODELS.map((m, i) => ({ ...m, value: 300 - i * 110 }))} order={ORDER} format={formatMoneyExact} centreLabel="this month" />
      <Heatmap title="When sessions run, last 4 weeks" summary="Sessions cluster on weekdays between 08:00 and 19:00" cells={cells} unit="Sessions" />
      <LineChart title="Loading" summary="Loading" series={spend} loading />
      <LineChart title="Empty" summary="No data" series={[]} emptyText={{ title: 'No spend yet', description: 'Spend appears once a session reports usage.' }} />
      <LineChart
        title="Error"
        summary="Error"
        series={[]}
        error={{ what: 'Could not load spend.', why: 'The hub did not answer within 10 s.', next: 'Check that Orgus is running, then try again.' }}
        onRetry={() => new Promise((r) => setTimeout(r, 600))}
      />
      <LineChart title="Partial" summary="Spend from two of three machines" series={spend.slice(0, 2)} order={ORDER} format={formatMoney} axisFormat={formatMoneyCompact} partial="build-01 is offline, so its spend is missing." />
    </div>
  );
}

/** The one formatter, on the values content.md lists. */
function FormatsSection() {
  const rows: Array<[string, string, string]> = [
    ['Count under 10,000', 'formatCount(1234)', formatCount(1234)],
    ['Count from 10,000', 'formatCount(12_345)', formatCount(12_345)],
    ['Never lakh grouping', 'formatInt(250_000)', formatInt(250_000)],
    ['Billions', 'formatCount(1_200_000_000)', formatCount(1_200_000_000)],
    ['Tokens', 'formatTokens(4_900)', formatTokens(4_900)],
    ['Money, totals', 'formatMoney(19_100)', formatMoney(19_100)],
    ['Money, per item', 'formatMoneyExact(826.1)', formatMoneyExact(826.1)],
    ['Under a cent', 'formatMoney(0.004)', formatMoney(0.004)],
    ['Percent', 'formatPercent(0.964, 1)', formatPercent(0.964, 1)],
    ['Negative', 'formatPercent(-0.03)', formatPercent(-0.03)],
    ['Duration', 'formatDuration(3_900_000)', formatDuration(3_900_000)],
    ['Relative', 'formatRelative(now - 7 min)', formatRelative(T0 - 7 * 60_000, T0)],
    ['Date', 'formatDate(ts)', formatDate(T0, T0)],
    ['Time', 'formatClock(ts)', formatClock(T0 + 15 * 3_600_000 + 4 * 60_000)],
    ['Size', 'formatBytes(1_200_000_000)', formatBytes(1_200_000_000)],
  ];
  return (
    <Panel title="The one formatter" sub="lib/format.ts: en-US always, suffixes K, M and B" data-catalogue="formats" flush>
      <table className="w-full border-collapse text-ui">
        <tbody>
          {rows.map(([kind, call, out]) => (
            <tr key={kind} className="h-row border-b border-border-subtle last:border-b-0">
              <td className="pl-4 text-fg-muted">{kind}</td>
              <td className="font-mono text-caption text-fg-subtle">{call}</td>
              <td className="pr-4 text-right font-medium text-fg num">{out}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

/** The shadcn layer, the table, the charts and the formatter, in every state (ADR 0033). */
export function FoundationCatalogue() {
  return (
    <div className="flex flex-col gap-5">
      <div className="grid items-start gap-5 xl:grid-cols-2">
        <ControlsSection />
        <OverlaySection />
        <DisclosureSection />
        <FormatsSection />
        <TableSection />
      </div>
      <ChartsSection />
    </div>
  );
}
